import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { LinearProgress } from '@atom/mui';
import colors from '@atom/styles/colors';
import { BudgetChart } from '@atom/types/budget';
import { numberToLocaleString } from '@atom/utilities/currencyUtility';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import ExpendituresChartContext from './ExpendituresChartContext';
import ChartLegend from './ExpendituresChartLegend';
import ChartTooltip from './ExpendituresChartTooltip';
import {
  BAR_ANIMATION_MS,
  CHART_HEIGHT,
  CHART_TOP_MARGIN,
  getChartColor,
  getFixedBarColor,
  LINE_ANIMATION_MS,
  renderLegendText,
  yAxisTickFormatter,
} from './expendituresChartUtils';
import { usePrepExpendituresChartData } from './usePrepExpendituresChartData';

const styles = {
  container: {
    height: `${CHART_HEIGHT}rem`,
  },
  progress: {
    margin: `${CHART_HEIGHT / 2}rem 35%`,
  },
};

interface Props {
  budgetsRaw: BudgetChart[];
  loading: boolean;
}

const ExpendituresChart = ({ budgetsRaw, loading }: Props) => {
  const {
    actualFields,
    futureFixedFields,
    remainingFields,
    futureFixedRemainingFields,
    maxVal,
    minVal,
    data,
  } = usePrepExpendituresChartData(budgetsRaw);

  return (
    <ExpendituresChartContext.Provider
      value={{
        actualFields,
        remainingFields,
        futureFixedFields,
        futureFixedRemainingFields,
        data,
      }}
    >
      <>
        {loading || isNilOrEmpty(budgetsRaw) ? (
          <div style={styles.container}>
            <LinearProgress style={styles.progress} />
          </div>
        ) : (
          <div style={styles.container}>
            <ComposedChart
              data={data}
              responsive
              height="100%"
              width="100%"
              margin={{ top: CHART_TOP_MARGIN }}
            >
              <CartesianGrid vertical={false} />
              <XAxis
                tickLine={false}
                axisLine={false}
                dataKey="name"
                scale="point"
                padding="gap"
                mirror={minVal < 0}
              />
              <YAxis
                tickLine={false}
                tickFormatter={tickVal => yAxisTickFormatter(tickVal, maxVal)}
              />
              <Legend formatter={renderLegendText} content={<ChartLegend />} />
              <Tooltip
                formatter={numberToLocaleString}
                content={<ChartTooltip />}
              />
              <ReferenceLine y={0} stroke={colors.neutral.black} />
              {actualFields.map((fieldName, idx) => (
                <Bar
                  dataKey={fieldName}
                  key={fieldName}
                  stackId={data[idx].name}
                  barSize={35}
                  fill={getChartColor(idx)}
                  animationDuration={BAR_ANIMATION_MS}
                />
              ))}
              {futureFixedFields.map((fieldName, idx) => (
                <Bar
                  dataKey={fieldName}
                  key={fieldName}
                  stackId={data[idx].name}
                  barSize={35}
                  fill={getFixedBarColor(idx)}
                  animationDuration={BAR_ANIMATION_MS}
                />
              ))}
              {remainingFields.map((fieldName, idx) => (
                <Line
                  type="monotone"
                  dataKey={fieldName}
                  key={fieldName}
                  stroke={getChartColor(idx)}
                  strokeWidth={2}
                  animationDuration={LINE_ANIMATION_MS}
                />
              ))}
              {futureFixedRemainingFields.map((fieldName, idx) => (
                <Line
                  type="monotone"
                  dataKey={fieldName}
                  key={fieldName}
                  stroke={getChartColor(idx)}
                  strokeWidth={2}
                  animationDuration={LINE_ANIMATION_MS}
                  strokeDasharray="5 5"
                  dot={{
                    stroke: getChartColor(idx),
                    strokeWidth: 2,
                    r: 3,
                    strokeDasharray: '',
                  }}
                />
              ))}
            </ComposedChart>
          </div>
        )}
      </>
    </ExpendituresChartContext.Provider>
  );
};

export default ExpendituresChart;
